import { useEffect, useState, useMemo } from "react";
import { Row } from "../../../../components/flex/styled";
import { Table } from "../../../../components/table";
import { P, Span } from "../../../../components/typography/styled";
import { Layout } from "../../../../containers/app/layout";
import { ReferralWrapper } from "./styled";
import { retrieveAllReferrals } from "../../../../utils/apis/referral/retrieveAllReferrals";
import Cookies from "universal-cookie";

export const Referral = () => {
  const PAGE_SIZE = 10;
  const cookies = new Cookies();
  const TOKEN = cookies.get("TOKEN");

  const columnHeaders = [
    "Referrer",
    "Employer",
    "Company Name",
    "Company Email",
    "Date",
    "Status",
  ];

  const [referrals, setReferrals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const fetchReferrals = async () => {
      try {
        const response = await retrieveAllReferrals(TOKEN);
        return setReferrals(response.data);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchReferrals();
  }, [TOKEN]);

  const totalReferrals = referrals?.length || 0;
  const totalPages = Math.max(1, Math.ceil(totalReferrals / 10));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedReferrals = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return referrals.slice(start, end);
  }, [referrals, currentPage]);

  const visiblePageNumbers = useMemo(() => {
    if (totalPages <= 3) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }
    if (currentPage <= 2) {
      return [1, 2, 3];
    }
    if (currentPage >= totalPages - 1) {
      return [totalPages - 2, totalPages - 1, totalPages];
    }
    return [currentPage - 1, currentPage, currentPage + 1];
  }, [currentPage, totalPages]);

  const rangeStart =
    totalReferrals === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(currentPage * PAGE_SIZE, totalReferrals);
  const referralCountText = totalReferrals === 1 ? "referral" : "referrals";

  return (
    <Layout id={"referral"} title={"Referrals"}>
      <ReferralWrapper>
        <Row className="heading-row">
          <div>
            <Span>Referral List</Span>
            <P>Track referred employers and their onboarding status.</P>
          </div>
          <span className="total-pill">{referrals?.length || 0} total</span>
        </Row>
        {isLoading ? (
          <div className="referral-state">Loading referrals...</div>
        ) : referrals?.length ? (
          <div className="referrals-table">
            <Table
              columnTitles={columnHeaders}
              rowItems={paginatedReferrals}
              location={"Referrals Table"}
            />
            <div className="table-footer">
              <span>
                Showing {rangeStart}-{rangeEnd} of {totalReferrals}{" "}
                {referralCountText}
              </span>
              <div className="pagination" aria-hidden="true">
                <button
                  type="button"
                  className="page-chip"
                  disabled={currentPage === 1}
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(1, prev - 1))
                  }
                >
                  &lt;
                </button>
                {visiblePageNumbers.map((pageNumber) => (
                  <button
                    key={pageNumber}
                    type="button"
                    className={`page-chip ${currentPage === pageNumber ? "active" : ""}`}
                    onClick={() => setCurrentPage(pageNumber)}
                  >
                    {pageNumber}
                  </button>
                ))}
                <button
                  type="button"
                  className="page-chip"
                  disabled={currentPage === totalPages}
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                  }
                >
                  &gt;
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="referral-state empty-state">
            <strong>No referrals yet</strong>
            <span>
              New employer referrals will appear here once they are submitted.
            </span>
          </div>
        )}
      </ReferralWrapper>
    </Layout>
  );
};
